import type {
  EstimateRequest,
  EstimateResult,
  PaymentSnapshot,
} from "./agent-types";
import type { DashboardSnapshot } from "./dashboard";
import type { EstimatePreviewResponse } from "./estimates";
import { extractUSStateCodeFromLocation } from "./us-location";

const DEFAULT_RATE_PERCENT = 6.75;
const DEFAULT_HOME_PRICE = 150_000;

export interface MonthlyPaymentFormState {
  customInterestRatePercent: number | null;
  downPaymentPercent: number;
  homePrice: number;
  loanTermYears: 15 | 30;
}

export interface FallbackPaymentPreview {
  downPaymentPercent: number;
  effectiveRatePercent: number;
  loanTermMonths: number;
  payment: PaymentSnapshot;
  sourceLabel: string;
  summary: string;
}

const PREVIEW_INSURANCE_RATE = 0.0042;
const PREVIEW_PMI_RATE = 0.0062;
const PREVIEW_PROPERTY_TAX_RATE = 0.0108;
const MAX_DISPLAY_DTI_RATIO = 1;

export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function dollarsToCents(value: number) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value * 100);
}

export function centsToDollars(value: number) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value / 100);
}

export function parseCurrencyDollarsInput(value: string) {
  const normalized = value.trim().replace(/[$,\s]/g, "");
  if (!normalized) {
    return null;
  }
  if (
    !/^-?\d*(?:\.\d*)?$/.test(normalized) ||
    normalized === "-" ||
    normalized === "." ||
    normalized === "-."
  ) {
    return null;
  }
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.round(parsed);
}

export function decimalRateToPercent(value: number | null | undefined) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }
  return Number((value * 100).toFixed(2));
}

export function capDtiRatio(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return clamp(value, 0, MAX_DISPLAY_DTI_RATIO);
}

export function dtiRatioToDisplayPercent(value: number | null | undefined) {
  const ratio = capDtiRatio(value);
  if (ratio === null) {
    return null;
  }
  return Number((ratio * 100).toFixed(2));
}

export function percentToDecimal(value: number) {
  return Number((value / 100).toFixed(5));
}

export function getDefaultMonthlyPaymentForm(
  snapshot: DashboardSnapshot,
): MonthlyPaymentFormState {
  const inferredDownPayment =
    snapshot.targetPrice > 0
      ? (snapshot.savings / snapshot.targetPrice) * 100
      : 10;

  return {
    customInterestRatePercent: null,
    downPaymentPercent: clamp(Number(inferredDownPayment.toFixed(1)), 3, 40),
    homePrice:
      snapshot.targetPrice > 0 ? snapshot.targetPrice : DEFAULT_HOME_PRICE,
    loanTermYears: 30,
  };
}

export function buildMonthlyPaymentEstimateRequest(
  snapshot: DashboardSnapshot,
  form: MonthlyPaymentFormState,
  asOf = new Date().toISOString(),
): EstimateRequest {
  const downPaymentPercent = clamp(form.downPaymentPercent, 3, 40);
  const loanTermMonths = form.loanTermYears * 12;

  return {
    mode: "enriched",
    borrower: {
      credit_bucket: snapshot.creditBucket ?? undefined,
      gross_annual_income: dollarsToCents(snapshot.householdIncome),
      liquid_assets: dollarsToCents(snapshot.savings),
      monthly_debts: dollarsToCents(snapshot.monthlyDebts),
      has_second_job: false,
      is_self_employed: false,
      has_bonus_income: false,
      has_commission_income: false,
    },
    property: {
      location_code: extractLocationCode(snapshot.targetArea),
      target_price: dollarsToCents(form.homePrice),
    },
    financing: {
      product: "conventional",
      purpose: "purchase",
      down_payment_percent: percentToDecimal(downPaymentPercent),
      down_payment_strategy: "fixed_percent",
      down_payment_amount: dollarsToCents(
        form.homePrice * (downPaymentPercent / 100),
      ),
      first_time_buyer: snapshot.isFirstTimeBuyer,
      subordinate_financing: false,
      veteran_eligible: false,
      ami_eligible: false,
      interest_rate_percent:
        typeof form.customInterestRatePercent === "number"
          ? percentToDecimal(form.customInterestRatePercent)
          : undefined,
      loan_term_months: loanTermMonths,
    },
    requested_products: ["conventional"],
    as_of: asOf,
  };
}

export function getPrimaryEstimate(
  preview: Pick<EstimatePreviewResponse, "results"> | null | undefined,
) {
  return preview?.results[0] ?? null;
}

export function getPaymentSnapshot(result: EstimateResult | null | undefined) {
  return result?.raw.payment_at_target_price ?? null;
}

export function getEffectiveRatePercent(
  result: EstimateResult | null | undefined,
) {
  return decimalRateToPercent(result?.raw.pricing.effective_rate);
}

export function getDownPaymentAmount(
  homePrice: number,
  downPaymentPercent: number,
) {
  return Math.round(homePrice * (downPaymentPercent / 100));
}

export function getPaymentBreakdown(snapshot: PaymentSnapshot | null) {
  if (!snapshot) {
    return [];
  }

  return [
    {
      key: "principal-interest",
      label: "Principal & interest",
      value: snapshot.principal_interest,
    },
    {
      key: "property-taxes",
      label: "Property taxes",
      value: snapshot.property_taxes,
    },
    { key: "insurance", label: "Home insurance", value: snapshot.insurance },
    {
      key: "mortgage-insurance",
      label: "PMI",
      value: snapshot.mortgage_insurance,
    },
    { key: "hoa", label: "HOA", value: snapshot.hoa },
  ].filter((item) => item.value > 0 || item.key === "mortgage-insurance");
}

export function formatCurrency(value: number) {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return `$${Math.round(value).toLocaleString()}`;
}

export function formatCurrencyFromCents(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "—";
  }
  return formatCurrency(centsToDollars(value));
}

export function formatPercent(value: number | null | undefined, digits = 2) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }
  return `${value.toFixed(digits)}%`;
}

export function getPaymentSummary(result: EstimateResult | null | undefined) {
  return (
    result?.labels.customer_summary ??
    "Adjust the sliders to see how your monthly payment mix changes."
  );
}

export function getInterestRateDisplay(
  customInterestRatePercent: number | null,
  result: EstimateResult | null | undefined,
) {
  return (
    customInterestRatePercent ??
    getEffectiveRatePercent(result) ??
    DEFAULT_RATE_PERCENT
  );
}

export function buildFallbackPaymentPreview(
  snapshot: DashboardSnapshot,
  form: MonthlyPaymentFormState,
  displayedRatePercent: number,
): FallbackPaymentPreview {
  const downPaymentPercent = clamp(form.downPaymentPercent, 3, 40);
  const loanAmount = Math.max(
    0,
    form.homePrice - getDownPaymentAmount(form.homePrice, downPaymentPercent),
  );
  const loanTermMonths = form.loanTermYears * 12;
  const principalInterest = amortizedMonthlyPayment(
    loanAmount,
    displayedRatePercent / 100,
    loanTermMonths,
  );
  const propertyTaxes = Math.round(
    (form.homePrice * PREVIEW_PROPERTY_TAX_RATE) / 12,
  );
  const insurance = Math.round((form.homePrice * PREVIEW_INSURANCE_RATE) / 12);
  const mortgageInsurance =
    downPaymentPercent < 20
      ? Math.round((loanAmount * PREVIEW_PMI_RATE) / 12)
      : 0;
  const total =
    principalInterest + propertyTaxes + insurance + mortgageInsurance;
  const comfortDelta = total - snapshot.monthlyBudget;

  return {
    downPaymentPercent,
    effectiveRatePercent: displayedRatePercent,
    loanTermMonths,
    payment: {
      hoa: 0,
      insurance: dollarsToCents(insurance),
      loan_amount: dollarsToCents(loanAmount),
      mortgage_insurance: dollarsToCents(mortgageInsurance),
      principal_interest: dollarsToCents(principalInterest),
      property_taxes: dollarsToCents(propertyTaxes),
      total: dollarsToCents(total),
      upfront_fee: 0,
    },
    sourceLabel: "Preview math",
    summary:
      comfortDelta > 0
        ? `${formatCurrency(comfortDelta)} above your current monthly comfort target.`
        : `${formatCurrency(Math.abs(comfortDelta))} below your current monthly comfort target.`,
  };
}

function extractLocationCode(targetArea: string) {
  return extractUSStateCodeFromLocation(targetArea) ?? "";
}

function amortizedMonthlyPayment(
  principal: number,
  annualRate: number,
  months: number,
) {
  if (principal <= 0 || months <= 0) {
    return 0;
  }

  const monthlyRate = annualRate / 12;

  if (monthlyRate === 0) {
    return Math.round(principal / months);
  }

  const factor = (1 + monthlyRate) ** months;
  return Math.round((principal * monthlyRate * factor) / (factor - 1));
}
